/**
 * RoomView — a `RoomDef` made visible.
 *
 * Owns the dollhouse: checkerboard floor (with real holes where the pits are),
 * low walls so a phone camera can see in, one instanced pair of draw calls per
 * prop type, and the handful of props that actually animate.
 *
 * It is used by all three modes. The builder rebuilds it on every edit; the
 * raid and the replay only call `update()`. Same room data, same picture — the
 * owner's preview cannot lie about what the raider will meet.
 */

import * as THREE from 'three'
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js'

import type { PropTypeId } from '../core/props'
import { PROP_ORDER, propType } from '../core/props'
import type { PropInstance, RoomDef } from '../core/room'
import { ROOM_CONFIG, cellToWorld, rotToDir } from '../core/room'
import { sweepOffset } from '../sim/config'
import { chestGeometry, fanBladeGeometry, propGeometry, swatterArmGeometry } from './geometry'
import { facet, outlineMaterial, toonMaterial } from './toon'

const FLOOR_LIGHT = 0xf3e2c4
const FLOOR_DARK = 0xe6cfa8
const WALL_COLOR = 0xd9b98d
const WALL_TOP = 0xf6ead6
const VISUAL_WALL_HEIGHT = 0.52

interface InstancedPair {
  body: THREE.InstancedMesh
  accent: THREE.InstancedMesh | null
}

interface AnimatedProp {
  prop: PropInstance
  arm?: THREE.Object3D
  blades?: THREE.Object3D
  period: number
}

export class RoomView {
  readonly root = new THREE.Group()
  private structure = new THREE.Group()
  private propLayer = new THREE.Group()
  private overlay = new THREE.Group()

  private instanced = new Map<PropTypeId, InstancedPair>()
  private animated: AnimatedProp[] = []
  private owned: THREE.BufferGeometry[] = []

  private chest: THREE.Group | null = null
  private door: THREE.Group | null = null
  private grid: THREE.LineSegments | null = null
  private preview: THREE.Group | null = null
  private previewOk = true
  private selection: THREE.Mesh | null = null
  private markers = new THREE.Group()

  private room: RoomDef

  constructor(room: RoomDef) {
    this.room = room
    this.root.add(this.structure, this.propLayer, this.overlay)
    this.overlay.add(this.markers)
    this.buildStructure()
    this.buildProps()
  }

  // --------------------------------------------------------------- structure

  private track<T extends THREE.BufferGeometry>(g: T): T {
    this.owned.push(g)
    return g
  }

  private buildStructure(): void {
    const { width, depth } = this.room
    const c = ROOM_CONFIG.cell
    const holes = new Set<string>()
    for (const p of this.room.props) {
      if (propType(p.type).behavior.type === 'pit') holes.add(`${p.x},${p.z}`)
    }

    // Two merged meshes, checkerboard. Holes are simply absent quads, matching
    // the physics floor exactly — the hole you see is the hole you fall into.
    const light: THREE.BufferGeometry[] = []
    const dark: THREE.BufferGeometry[] = []
    for (let z = 0; z < depth; z++) {
      for (let x = 0; x < width; x++) {
        if (holes.has(`${x},${z}`)) continue
        const w = cellToWorld(x, z, width, depth)
        const quad = new THREE.PlaneGeometry(c, c)
        quad.rotateX(-Math.PI / 2)
        quad.translate(w.x, 0, w.z)
        ;((x + z) % 2 === 0 ? light : dark).push(quad)
      }
    }
    for (const [parts, color] of [
      [light, FLOOR_LIGHT],
      [dark, FLOOR_DARK],
    ] as const) {
      if (parts.length === 0) continue
      const merged = mergeAll(parts)
      const mesh = new THREE.Mesh(this.track(merged), toonMaterial(color))
      mesh.receiveShadow = true
      this.structure.add(mesh)
    }

    const halfW = (width * c) / 2
    const halfD = (depth * c) / 2
    const t = 0.16
    const h = VISUAL_WALL_HEIGHT
    const wallSpecs: Array<[number, number, number, number, number]> = [
      [halfW + t, h, t, 0, -halfD - t / 2],
      [halfW + t, h, t, 0, halfD + t / 2],
      [t, h, halfD + t, -halfW - t / 2, 0],
      [t, h, halfD + t, halfW + t / 2, 0],
    ]
    for (const [hx, hy, hz, x, z] of wallSpecs) {
      const g = this.track(facet(new THREE.BoxGeometry(hx * 2, hy, hz * 2)))
      const mesh = new THREE.Mesh(g, toonMaterial(WALL_COLOR))
      mesh.position.set(x, hy / 2, z)
      mesh.receiveShadow = true
      mesh.castShadow = true
      this.structure.add(mesh)
    }

    // Doorway: a gap in the wall plus a frame, so "where you came in" and
    // "where you must get back to" are the same unmistakable landmark.
    const dw = cellToWorld(this.room.door.x, this.room.door.z, width, depth)
    const outward = this.room.door.z === 0 ? -1 : 1
    const frame = new THREE.Group()
    const jamb = this.track(facet(new THREE.BoxGeometry(0.14, 0.86, 0.2)))
    for (const sx of [-0.48, 0.48]) {
      const m = new THREE.Mesh(jamb, toonMaterial(0xa9714a))
      m.position.set(sx, 0.43, 0)
      m.castShadow = true
      frame.add(m)
    }
    const lintel = new THREE.Mesh(
      this.track(facet(new THREE.BoxGeometry(1.1, 0.14, 0.2))),
      toonMaterial(0xa9714a),
    )
    lintel.position.y = 0.93
    frame.add(lintel)
    const mat = new THREE.Mesh(
      this.track(facet(new THREE.BoxGeometry(1.0, 0.04, 0.5))),
      toonMaterial(WALL_TOP),
    )
    mat.position.set(0, 0.02, outward * 0.34)
    frame.add(mat)
    frame.position.set(dw.x, 0, dw.z)
    this.structure.add(frame)
    this.door = frame

    this.buildChest()
    this.buildGrid()
  }

  private buildChest(): void {
    const geo = chestGeometry()
    this.track(geo.body)
    this.track(geo.accent)
    const group = new THREE.Group()
    const body = new THREE.Mesh(geo.body, toonMaterial(0xb98a52))
    const accent = new THREE.Mesh(geo.accent, toonMaterial(0xffd166))
    body.castShadow = true
    accent.castShadow = true
    const shell = new THREE.Mesh(geo.body, outlineMaterial())
    shell.scale.setScalar(1.07)
    group.add(body, accent, shell)
    const w = cellToWorld(this.room.chest.x, this.room.chest.z, this.room.width, this.room.depth)
    group.position.set(w.x, 0, w.z)
    this.chest = group
    this.structure.add(group)
  }

  private buildGrid(): void {
    const { width, depth } = this.room
    const c = ROOM_CONFIG.cell
    const pts: number[] = []
    const hw = (width * c) / 2
    const hd = (depth * c) / 2
    for (let x = 0; x <= width; x++) {
      const wx = -hw + x * c
      pts.push(wx, 0.012, -hd, wx, 0.012, hd)
    }
    for (let z = 0; z <= depth; z++) {
      const wz = -hd + z * c
      pts.push(-hw, 0.012, wz, hw, 0.012, wz)
    }
    const g = this.track(new THREE.BufferGeometry())
    g.setAttribute('position', new THREE.Float32BufferAttribute(pts, 3))
    const grid = new THREE.LineSegments(
      g,
      new THREE.LineBasicMaterial({ color: 0xb08d63, transparent: true, opacity: 0.5 }),
    )
    grid.visible = false
    this.grid = grid
    this.overlay.add(grid)
  }

  // -------------------------------------------------------------------- props

  private buildProps(): void {
    const byType = new Map<PropTypeId, PropInstance[]>()
    for (const p of this.room.props) {
      const list = byType.get(p.type)
      if (list) list.push(p)
      else byType.set(p.type, [p])
    }

    const m = new THREE.Matrix4()
    for (const type of PROP_ORDER) {
      const list = byType.get(type)
      if (!list || list.length === 0) continue
      const t = propType(type)
      const geo = propGeometry(type)
      const body = new THREE.InstancedMesh(geo.body, toonMaterial(t.color), list.length)
      body.castShadow = true
      body.receiveShadow = true
      const accent = geo.accent
        ? new THREE.InstancedMesh(geo.accent, toonMaterial(t.accent), list.length)
        : null
      if (accent) accent.castShadow = true

      list.forEach((p, i) => {
        const w = cellToWorld(p.x, p.z, this.room.width, this.room.depth)
        m.makeRotationY((p.rot * Math.PI) / 2)
        m.setPosition(w.x, 0, w.z)
        body.setMatrixAt(i, m)
        accent?.setMatrixAt(i, m)
      })
      body.instanceMatrix.needsUpdate = true
      body.computeBoundingSphere()
      if (accent) {
        accent.instanceMatrix.needsUpdate = true
        accent.computeBoundingSphere()
      }
      this.propLayer.add(body)
      if (accent) this.propLayer.add(accent)
      this.instanced.set(type, { body, accent })
    }

    // The moving parts. Capped by the per-room prop limits, so these stay in
    // the single digits and can afford to be individual objects.
    for (const p of this.room.props) {
      const t = propType(p.type)
      if (t.behavior.type === 'sweep') {
        const arm = new THREE.Mesh(this.track(facet(swatterArmGeometry())), toonMaterial(t.accent))
        arm.castShadow = true
        const w = cellToWorld(p.x, p.z, this.room.width, this.room.depth)
        arm.position.set(w.x, 0.86, w.z)
        this.propLayer.add(arm)
        this.animated.push({ prop: p, arm, period: t.behavior.period })
      } else if (t.behavior.type === 'push') {
        const blades = new THREE.Mesh(this.track(facet(fanBladeGeometry())), toonMaterial(0xf2f7ff))
        const w = cellToWorld(p.x, p.z, this.room.width, this.room.depth)
        const dir = rotToDir(p.rot)
        blades.position.set(w.x + dir.dx * 0.04, 0.52, w.z + dir.dz * 0.04)
        blades.rotation.y = (p.rot * Math.PI) / 2
        this.propLayer.add(blades)
        this.animated.push({ prop: p, blades, period: 1 })
      }
    }
  }

  // ------------------------------------------------------------------ updates

  /** Rebuild after a builder edit. Cheap enough at this room size to be atomic. */
  rebuild(room: RoomDef): void {
    this.room = room
    const gridWasVisible = this.grid?.visible ?? false
    this.clearBuilt()
    this.buildStructure()
    this.buildProps()
    if (this.grid) this.grid.visible = gridWasVisible
  }

  update(time: number): void {
    for (const a of this.animated) {
      if (a.arm) {
        const dir = rotToDir(a.prop.rot)
        const base = Math.atan2(dir.dx, dir.dz)
        // Same function the sim uses, so the arm's contact frame is honest.
        a.arm.rotation.y = base + sweepOffset(time, a.period) * 1.05
      }
      if (a.blades) a.blades.rotation.z = time * 9
    }
    if (this.chest) {
      this.chest.position.y = 0.06 + Math.sin(time * 2.2) * 0.045
      this.chest.rotation.y = Math.sin(time * 0.7) * 0.25
    }
  }

  setChestVisible(visible: boolean): void {
    if (this.chest) this.chest.visible = visible
  }

  setDoorLocked(locked: boolean): void {
    if (!this.door) return
    this.door.traverse((o) => {
      if (o instanceof THREE.Mesh && o.material instanceof THREE.MeshToonMaterial) {
        o.material = toonMaterial(locked ? 0x7b4b3a : 0xa9714a)
      }
    })
  }

  setGridVisible(visible: boolean): void {
    if (this.grid) this.grid.visible = visible
  }

  /** Ghost of the prop about to be placed. `ok` colours it green or red. */
  setPreview(type: PropTypeId | null, x: number, z: number, rot: number, ok: boolean): void {
    if (this.preview) {
      this.preview.removeFromParent()
      this.preview = null
    }
    if (!type) return
    const geo = propGeometry(type)
    const group = new THREE.Group()
    const mat = new THREE.MeshBasicMaterial({
      color: ok ? 0x4fae72 : 0xd8524b,
      transparent: true,
      opacity: 0.55,
      depthWrite: false,
    })
    group.add(new THREE.Mesh(geo.body, mat))
    if (geo.accent) group.add(new THREE.Mesh(geo.accent, mat))
    const w = cellToWorld(x, z, this.room.width, this.room.depth)
    group.position.set(w.x, 0.02, w.z)
    group.rotation.y = (rot * Math.PI) / 2
    this.previewOk = ok
    this.preview = group
    this.overlay.add(group)
  }

  get isPreviewValid(): boolean {
    return this.previewOk
  }

  /** Ring under the currently selected prop, for move/rotate/delete. */
  setSelection(cell: { x: number; z: number } | null): void {
    if (!this.selection) {
      const g = this.track(new THREE.RingGeometry(0.38, 0.5, 20))
      g.rotateX(-Math.PI / 2)
      this.selection = new THREE.Mesh(
        g,
        new THREE.MeshBasicMaterial({
          color: 0x3f7fd8,
          transparent: true,
          opacity: 0.85,
          depthWrite: false,
        }),
      )
      this.overlay.add(this.selection)
    }
    if (!cell) {
      this.selection.visible = false
      return
    }
    const w = cellToWorld(cell.x, cell.z, this.room.width, this.room.depth)
    this.selection.visible = true
    this.selection.position.set(w.x, 0.02, w.z)
  }

  /**
   * Hesitation pins. This is the owner's real feedback channel: not a clear
   * rate, but the exact spots where a stranger stopped and doubted themselves.
   */
  setMarkers(points: ReadonlyArray<{ x: number; z: number }>): void {
    this.markers.clear()
    if (points.length === 0) return
    const geo = this.track(new THREE.ConeGeometry(0.13, 0.34, 6))
    const mat = new THREE.MeshBasicMaterial({ color: 0x3f7fd8, transparent: true, opacity: 0.8 })
    for (const p of points) {
      const pin = new THREE.Mesh(geo, mat)
      pin.rotation.x = Math.PI
      pin.position.set(p.x, 0.45, p.z)
      this.markers.add(pin)
    }
  }

  // ------------------------------------------------------------------ cleanup

  private clearBuilt(): void {
    for (const g of [this.structure, this.propLayer]) {
      g.traverse((o) => {
        if (o instanceof THREE.Mesh || o instanceof THREE.InstancedMesh) {
          if (o instanceof THREE.InstancedMesh) o.dispose()
        }
      })
      g.clear()
    }
    this.instanced.clear()
    this.animated = []
    this.chest = null
    this.door = null
    // The grid lives in `overlay` but its geometry is tracked; rebuild makes a
    // fresh one, so drop the old node here.
    this.grid?.removeFromParent()
    this.grid = null
    for (const g of this.owned) g.dispose()
    this.owned = []
  }

  dispose(): void {
    this.clearBuilt()
    this.preview?.removeFromParent()
    this.selection?.removeFromParent()
    this.markers.clear()
    this.root.removeFromParent()
  }
}

/** One draw call per floor colour: 144 quads in, one buffer out. */
function mergeAll(parts: THREE.BufferGeometry[]): THREE.BufferGeometry {
  if (parts.length === 1) return facet(parts[0]!)
  const merged = mergeGeometries(parts)
  for (const p of parts) p.dispose()
  return facet(merged ?? new THREE.BufferGeometry())
}
